/**
 * Shared PIN-lock UI for report templates (list modal + wizard).
 */
import React from 'react';
import { patchJSON, postJSON } from '../../api/client.js';
import {
  UniCheckField, UniFormError, UniModal, UniTextField, useUniAction,
} from './UniShared.jsx';

const EP_REPORTS = '/api/uni-forms/reports/';

export function ReportTemplatePinLockFields({
  pinLocked, onPinLockedChange,
  editPin, onEditPinChange,
  editPinConfirm, onEditPinConfirmChange,
  hasPin = false,
}) {
  return (
    <>
      <p className="uni-card__sub">
        Lock the template so only someone with the PIN can edit or delete it.
        Viewing, running, sharing, duplicating, and CSV/XLSX export stay available.
      </p>
      <UniCheckField
        label="Lock template with a PIN"
        checked={pinLocked}
        onChange={onPinLockedChange}
      />
      {pinLocked && (
        <div className="uni-card__grid">
          <UniTextField
            label={hasPin ? 'New PIN (leave blank to keep current)' : 'PIN'}
            value={editPin}
            onChange={onEditPinChange}
          />
          <UniTextField
            label="Confirm PIN"
            value={editPinConfirm}
            onChange={onEditPinConfirmChange}
          />
        </div>
      )}
    </>
  );
}

function ModalFooter({ busy, onCancel, onSave, saveLabel = 'Save', disabled }) {
  return (
    <>
      <button type="button" className="ty-btn ty-btn--secondary"
              onClick={onCancel} disabled={busy}>
        Cancel
      </button>
      <button type="button" className="ty-btn" onClick={onSave}
              disabled={busy || disabled}>
        {busy ? 'Saving…' : saveLabel}
      </button>
    </>
  );
}

export function ReportTemplateLockModal({
  open, view, onClose, onSaved,
}) {
  const lockAction = useUniAction();
  const [mode, setMode] = React.useState('settings');
  const [verifyPin, setVerifyPin] = React.useState('');
  const [draft, setDraft] = React.useState({
    pin_locked: false,
    edit_pin: '',
    edit_pin_confirm: '',
    has_pin: false,
  });

  React.useEffect(() => {
    if (!open || !view) return;
    setVerifyPin('');
    setDraft({
      pin_locked: !!view.pin_locked,
      edit_pin: '',
      edit_pin_confirm: '',
      has_pin: !!view.has_pin,
    });
    setMode(view.pin_locked && !view.is_pin_unlocked ? 'verify' : 'settings');
  }, [open, view]);

  const validateDraft = () => {
    if (draft.pin_locked) {
      if (!draft.has_pin && !draft.edit_pin.trim()) {
        return 'Set a PIN before locking the template.';
      }
      if (draft.edit_pin && draft.edit_pin !== draft.edit_pin_confirm) {
        return 'PIN confirmation does not match.';
      }
    }
    return null;
  };

  const saveLock = () => {
    const validationError = validateDraft();
    if (validationError) {
      lockAction.setError(new Error(validationError));
      return;
    }
    lockAction.run(async () => {
      const body = { pin_locked: !!draft.pin_locked };
      if (draft.edit_pin) body.edit_pin = draft.edit_pin;
      const updated = await patchJSON(`${EP_REPORTS}${view.id}/`, body);
      onSaved?.(updated);
      onClose?.();
    }).catch(() => {});
  };

  const submitVerify = () => lockAction.run(async () => {
    const updated = await postJSON(`${EP_REPORTS}${view.id}/verify-pin/`, {
      pin: verifyPin,
    });
    setDraft({
      pin_locked: !!updated.pin_locked,
      edit_pin: '',
      edit_pin_confirm: '',
      has_pin: !!updated.has_pin,
    });
    setMode('settings');
    setVerifyPin('');
    onSaved?.(updated);
  }).catch(() => {});

  if (!open) return null;

  return (
    <UniModal
      open={open}
      title={view ? `PIN lock — ${view.name}` : 'PIN lock'}
      onClose={onClose}
      footer={
        mode === 'verify' ? (
          <ModalFooter
            busy={lockAction.busy}
            onCancel={onClose}
            onSave={submitVerify}
            saveLabel="Continue"
            disabled={!verifyPin.trim()}
          />
        ) : (
          <ModalFooter
            busy={lockAction.busy}
            onCancel={onClose}
            onSave={saveLock}
            saveLabel="Save lock settings"
          />
        )
      }
    >
      <UniFormError error={lockAction.error} />
      {mode === 'verify' ? (
        <>
          <p className="uni-card__sub">
            This template is PIN-locked. Enter the current PIN to change lock
            settings or remove the lock.
          </p>
          <UniTextField
            label="Current PIN"
            value={verifyPin}
            onChange={setVerifyPin}
            autoFocus
          />
        </>
      ) : (
        <ReportTemplatePinLockFields
          pinLocked={draft.pin_locked}
          onPinLockedChange={(v) => setDraft((d) => ({ ...d, pin_locked: v }))}
          editPin={draft.edit_pin}
          onEditPinChange={(v) => setDraft((d) => ({ ...d, edit_pin: v }))}
          editPinConfirm={draft.edit_pin_confirm}
          onEditPinConfirmChange={(v) => setDraft((d) => ({ ...d, edit_pin_confirm: v }))}
          hasPin={draft.has_pin}
        />
      )}
    </UniModal>
  );
}

export function ReportTemplatePinBadge({ locked }) {
  if (!locked) return null;
  return (
    <span
      className="uni-status-pill uni-status-pill--violet"
      title="PIN locked — edit and delete require the PIN"
      style={{ marginLeft: 8, fontSize: '0.85em' }}
    >
      🔒 Locked
    </span>
  );
}
